import { useEffect, useId, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Modal } from '@atoms/Modal';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { normalizeScannedCode, playScanBeep } from '@utils/scan.util';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  title?: string;
  closeOnScan?: boolean;
  showFileScan?: boolean;
}

export function BarcodeScanner({
  open,
  onClose,
  onScan,
  title = 'Barkod Tara',
  closeOnScan = true,
  showFileScan = true,
}: BarcodeScannerProps) {
  const regionId = useId().replace(/:/g, '');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastScanRef = useRef('');
  const [manual, setManual] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);

  const emitScan = (raw: string) => {
    const code = normalizeScannedCode(raw);
    if (!code || code === lastScanRef.current) return;
    lastScanRef.current = code;
    playScanBeep();
    onScan(code);
    if (closeOnScan) onClose();
    setTimeout(() => {
      lastScanRef.current = '';
    }, 1200);
  };

  const stopScanner = async () => {
    if (scannerRef.current?.isScanning) {
      try {
        await scannerRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    scannerRef.current = null;
    setCameraActive(false);
  };

  useEffect(() => {
    if (!open) {
      void stopScanner();
      setManual('');
      setError(null);
      return;
    }

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(regionId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 15, qrbox: { width: 280, height: 160 } },
          (decoded) => emitScan(decoded),
          () => {},
        );
        setCameraActive(true);
        setError(null);
      } catch {
        setError('Kamera açılamadı. Manuel barkod girebilir veya fotoğraftan okuyabilirsiniz.');
        setCameraActive(false);
      }
    };

    void start();
    return () => {
      void stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, regionId]);

  const handleManual = () => {
    if (!manual.trim()) return;
    emitScan(manual);
  };

  const handleFileScan = async (file: File) => {
    setFileBusy(true);
    setError(null);
    try {
      const scanner = scannerRef.current ?? new Html5Qrcode(regionId);
      if (!scannerRef.current) scannerRef.current = scanner;
      const decoded = await scanner.scanFile(file, true);
      emitScan(decoded);
    } catch {
      setError('Fotoğrafta barkod veya QR kod bulunamadı.');
    } finally {
      setFileBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div id={regionId} className="overflow-hidden rounded-lg bg-black min-h-[200px]" />
        {!cameraActive && error && <p className="text-sm text-amber-600">{error}</p>}
        {showFileScan && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            loading={fileBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            Fotoğraftan Oku
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handleFileScan(file);
          }}
        />
        <div className="flex gap-2">
          <Input
            label="Manuel barkod / QR"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleManual();
            }}
            placeholder="8690000000000"
            className="flex-1"
          />
          <div className="flex items-end">
            <Button onClick={handleManual}>OK</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
